import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Save, RotateCcw, Code, Eye, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/use-auth';
import { useToast } from '@/hooks/use-toast';
import { getNewsletterTemplate, saveNewsletterTemplate } from '@/lib/firebase';
import { logger } from '@/lib/logger';
import { DEFAULT_NEWSLETTER_TEMPLATE } from '@/lib/newsletter-template-engine';

export default function NewsletterTemplatePage() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();

  const [template, setTemplate] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    loadTemplate();
  }, []);

  const loadTemplate = async () => {
    try {
      const data = await getNewsletterTemplate();
      setTemplate(data?.html || DEFAULT_NEWSLETTER_TEMPLATE);
    } catch (error: any) {
      logger.error('Error loading template:', error);
      setTemplate(DEFAULT_NEWSLETTER_TEMPLATE);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveNewsletterTemplate({ html: template, updated: new Date() });
      toast({ title: 'Template saved', description: 'Newsletter HTML template has been saved.' });
    } catch (error: any) {
      logger.error('Error saving template:', error);
      toast({ title: 'Failed to save template', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = () => {
    if (!confirm('Reset to the default template? Any custom changes will be lost.')) return;
    setTemplate(DEFAULT_NEWSLETTER_TEMPLATE);
    toast({ title: 'Template reset', description: 'Template has been reset to the default. Save to persist.' });
  };

  if (loading) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-center text-slate-600">Loading template...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => setLocation('/newsletters')}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Code className="h-6 w-6" />
                Newsletter HTML Template
              </h1>
              <p className="text-slate-600 mt-1">
                Edit the HTML template used to generate newsletter emails
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleResetToDefault}>
              <RotateCcw className="h-4 w-4 mr-1" />
              Reset to Default
            </Button>
            <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
              <Eye className="h-4 w-4 mr-1" />
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              Save Template
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Available Placeholders</CardTitle>
            <CardDescription>Use these in your template to insert newsletter data</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="font-semibold mb-1">General</p>
                <code className="text-xs block">{'{{emailSubject}}'}</code>
                <code className="text-xs block">{'{{emailSubheading}}'}</code>
                <code className="text-xs block">{'{{newsletterDate}}'}</code>
                <code className="text-xs block">{'{{{logoUrl}}}'}</code>
                <code className="text-xs block">{'{{{downloadUrlAppStore}}}'}</code>
                <code className="text-xs block">{'{{{downloadUrlGooglePlay}}}'}</code>
              </div>
              <div>
                <p className="font-semibold mb-1">Lead Article</p>
                <code className="text-xs block">{'{{leadArticle.title}}'}</code>
                <code className="text-xs block">{'{{{leadArticle.imageUrl}}}'}</code>
                <code className="text-xs block">{'{{#leadArticle.text}}{{.}}{{/leadArticle.text}}'}</code>
                <code className="text-xs block">{'{{#leadArticle.links}}{{text}} {{url}}{{/leadArticle.links}}'}</code>
              </div>
              <div>
                <p className="font-semibold mb-1">Features Loop</p>
                <code className="text-xs block">{'{{#features}}...{{/features}}'}</code>
                <code className="text-xs block">{'{{title}}, {{featureText}}, {{featureTag}}'}</code>
                <code className="text-xs block">{'{{{imageUrl}}}, {{description}}'}</code>
                <code className="text-xs block">{'{{featureType}}, {{displayOrder}}'}</code>
              </div>
              <div>
                <p className="font-semibold mb-1">Event Calendar Loop</p>
                <code className="text-xs block">{'{{#eventCalendar}}...{{/eventCalendar}}'}</code>
                <code className="text-xs block">{'{{title}}, {{dateTime}}'}</code>
                <code className="text-xs block">{'{{{imageUrl}}}, {{eventType}}'}</code>
                <code className="text-xs block">{'{{subtitle}}'}</code>
              </div>
              <div>
                <p className="font-semibold mb-1">Notes</p>
                <p className="text-xs text-slate-500">Use triple braces {'{{{...}}}'} for URLs to avoid HTML escaping.</p>
                <p className="text-xs text-slate-500">{'{{.}}'} refers to the current item in simple arrays (text paragraphs).</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className={showPreview ? 'grid grid-cols-2 gap-6' : ''}>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">HTML Template</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                className="font-mono text-xs min-h-[600px] resize-y"
                spellCheck={false}
              />
            </CardContent>
          </Card>

          {showPreview && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Template Preview</CardTitle>
                <CardDescription>Shows raw template with placeholder tokens</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden bg-white">
                  <iframe
                    srcDoc={template}
                    className="w-full min-h-[600px] border-0"
                    title="Template Preview"
                    sandbox="allow-same-origin"
                  />
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
